package top.wcpe.allincore.ui.runtime.element

import top.wcpe.allincore.ui.runtime.reactive.Effect
import top.wcpe.allincore.ui.runtime.reactive.EffectBody
import top.wcpe.allincore.ui.runtime.reactive.Signal
import kotlin.jvm.JvmStatic

/**
 * Task 2c-E1：可拖拽分隔容器（Splitter / ResizeBox）。
 *
 * 语义：容器内并排两栏（pane1 / pane2）与一条可拖拽分隔条；拖动分隔条改变两栏的相对尺寸。
 * 引擎只维护「分隔比例」[ratio]（0..1，pane1 占容器主轴的比例），并提供：
 * - [bindRatio]：把 ratio 双向绑定到 state 信号（与 Slider.value / ScrollView.scrollY 同款），
 *   作者层用 `width = state.ratio * total` 给 pane1 定尺寸，pane2 用 flexGrow 吃剩余；
 * - [dragBy]：分隔条拖拽时按像素位移调整 ratio（需先告知容器主轴总长 [total]），
 *   由 `ui("id").dragBy(event.deltaX)` 在分隔条 onDrag 中调用；
 * - [minRatio] / [maxRatio]：限制拖拽范围（默认 0.1 .. 0.9，避免某栏被拖没）。
 *
 * 布局上 Splitter 是普通容器（用 BoxRenderer 渲染、按 row/column 排两栏与分隔条）；
 * 「拖动改尺寸」由 ratio + 作者层尺寸绑定 + 现有 onDrag 事件共同达成，无需新增布局算法或协议字段。
 */
class Splitter private constructor() : UIElement<Splitter>() {

    /** 方向：true=水平（左右两栏，拖动改宽度），false=垂直（上下两栏，拖动改高度）。 */
    private var horizontalValue = true

    /** pane1 占主轴的比例（0..1）。 */
    private var ratioValue = DEFAULT_RATIO

    /** 比例下限（默认 0.1）。 */
    private var minRatioValue = DEFAULT_MIN_RATIO

    /** 比例上限（默认 0.9）。 */
    private var maxRatioValue = DEFAULT_MAX_RATIO

    /**
     * 容器主轴总长（像素），由作者层 `total = 300` 写入或与 pane 尺寸保持一致。
     * dragBy 用它把像素位移换算成比例增量；total <= 0 时 dragBy 静默忽略（无法换算）。
     */
    private var totalValue = 0f

    /** ratio 绑定的 state 信号；非空时 ratio 变化回写、信号变化驱动 ratio。 */
    private var boundRatioSignal: Signal<Any?>? = null

    /** 防回写环：正在把信号值写入元素时，不再反向写信号。 */
    private var applyingBoundRatio = false

    override fun self(): Splitter = this

    // -------- 方向 --------

    fun horizontal(v: Boolean): Splitter {
        horizontalValue = v
        return this
    }

    fun horizontal(): Boolean = horizontalValue

    /** 设置方向字符串："horizontal" / "vertical"（其余值按水平兜底）。 */
    fun orientation(v: String): Splitter {
        horizontalValue = !"vertical".equals(v, ignoreCase = true)
        return this
    }

    // -------- 比例 --------

    /** 设置 pane1 比例（自动钳制到 [minRatio, maxRatio]）。 */
    fun ratio(v: Double): Splitter {
        val old = ratioValue
        ratioValue = clampRatio(v)
        if (old.compareTo(ratioValue) != 0) {
            writeBackRatio()
        }
        return this
    }

    fun ratio(): Double = ratioValue

    fun minRatio(v: Double): Splitter {
        minRatioValue = v.coerceIn(RATIO_FLOOR, RATIO_CEIL)
        ratioValue = clampRatio(ratioValue)
        return this
    }

    fun minRatio(): Double = minRatioValue

    fun maxRatio(v: Double): Splitter {
        maxRatioValue = v.coerceIn(RATIO_FLOOR, RATIO_CEIL)
        ratioValue = clampRatio(ratioValue)
        return this
    }

    fun maxRatio(): Double = maxRatioValue

    fun total(v: Float): Splitter {
        totalValue = maxOf(0f, v)
        return this
    }

    fun total(): Float = totalValue

    /**
     * 分隔条拖拽：按像素位移调整 ratio。
     *
     * deltaPx 为分隔条沿主轴的位移（水平时取 event.deltaX，垂直时取 event.deltaY）；
     * 用 [total] 换算成比例增量后累加并钳制。total <= 0 时无法换算，静默忽略。
     */
    fun dragBy(deltaPx: Float): Splitter {
        if (totalValue <= 0f) {
            return this
        }
        return ratio(ratioValue + deltaPx / totalValue)
    }

    /** 钳制比例到 [minRatio, maxRatio]，并兜底 min <= max。 */
    private fun clampRatio(v: Double): Double {
        val lo = minOf(minRatioValue, maxRatioValue)
        val hi = maxOf(minRatioValue, maxRatioValue)
        return v.coerceIn(lo, hi)
    }

    // -------- 双向绑定（特性：ratio = state.x） --------

    /**
     * 双向绑定 ratio 到 state 信号：
     * - state→元素：注册 Effect，信号变化时写入 ratio（标记防环）；
     * - 元素→state：dragBy / ratio 变化时经 [writeBackRatio] 回写信号。
     */
    @Suppress("UNCHECKED_CAST")
    fun bindRatio(signal: Signal<*>?): Splitter {
        if (signal == null) {
            return this
        }
        val objectSignal = signal as Signal<Any?>
        boundRatioSignal = objectSignal
        val effect = Effect(
            EffectBody {
                val v = objectSignal.get()
                if (v !is Number) {
                    return@EffectBody
                }
                applyingBoundRatio = true
                try {
                    ratio(v.toDouble())
                } finally {
                    applyingBoundRatio = false
                }
            },
        )
        lifecycleScopeField.register(effect)
        return this
    }

    /** 把当前 ratio 回写到绑定信号（值未变 / 防环期间不写）。 */
    private fun writeBackRatio() {
        val signal = boundRatioSignal
        if (applyingBoundRatio || signal == null) {
            return
        }
        val cur = signal.get()
        val curD = if (cur is Number) cur.toDouble() else Double.NaN
        if (curD != ratioValue) {
            signal.set(ratioValue)
        }
    }

    companion object {
        /** 默认分隔比例。 */
        private const val DEFAULT_RATIO = 0.5

        /** 默认比例下限。 */
        private const val DEFAULT_MIN_RATIO = 0.1

        /** 默认比例上限。 */
        private const val DEFAULT_MAX_RATIO = 0.9

        /** 比例可取值地板。 */
        private const val RATIO_FLOOR = 0.0

        /** 比例可取值天花板。 */
        private const val RATIO_CEIL = 1.0

        @JvmStatic
        fun create(): Splitter = Splitter()
    }
}
